import { useEffect, useMemo, useRef, useState } from "react";
import { ContentHeader } from "../components/ContentHeader";
import { useData } from "../data/DataContext";
import { useUI } from "../data/UIContext";
import { OPERATIONS } from "../nav";
import type { AnyEntity, BackendOrSurfaceEntity, IntegrationEntity } from "../api/types";

type NodeKind = "service" | "integration" | "infra";

interface GraphNode {
  id: string;
  label: string;
  kind: NodeKind;
  tier?: string | null;
  item?: AnyEntity;
  x: number; y: number; vx: number; vy: number;
}
interface GraphLink { source: number; target: number }

const W = 1200;
const H = 700;

export function GraphTab() {
  const meta = OPERATIONS.find((e) => e.id === "graph")!;
  const data = useData();
  const ui = useUI();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [highlighted, setHighlighted] = useState<Set<number> | null>(null);

  const { nodes, links } = useMemo(() => buildGraph(data.commandCenter), [data.commandCenter]);

  // Run the force simulation once on mount (or when graph changes).
  // We mutate the node array in-place; React doesn't re-render, but the SVG
  // text/circles read .x/.y from the nodes via a ref to fresh values.
  useEffect(() => {
    if (nodes.length === 0) return;
    for (let tick = 0; tick < 300; tick++) {
      // Repulsion
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d2 = dx * dx + dy * dy + 0.01;
          const f = 1500 / d2;
          const d = Math.sqrt(d2);
          a.vx += (dx / d) * f; a.vy += (dy / d) * f;
          b.vx -= (dx / d) * f; b.vy -= (dy / d) * f;
        }
      }
      // Attraction along links
      for (const l of links) {
        const a = nodes[l.source], b = nodes[l.target];
        const dx = b.x - a.x, dy = b.y - a.y;
        const d = Math.sqrt(dx * dx + dy * dy) || 1;
        const k = 0.04 * (d - 80);
        a.vx += (dx / d) * k; a.vy += (dy / d) * k;
        b.vx -= (dx / d) * k; b.vy -= (dy / d) * k;
      }
      // Gravity to center
      for (const n of nodes) {
        n.vx += (W / 2 - n.x) * 0.005;
        n.vy += (H / 2 - n.y) * 0.005;
      }
      // Damping + integrate + bounds
      for (const n of nodes) {
        n.vx *= 0.7; n.vy *= 0.7;
        n.x = Math.max(20, Math.min(W - 20, n.x + n.vx));
        n.y = Math.max(20, Math.min(H - 20, n.y + n.vy));
      }
    }
    // Force a re-render once the simulation is done.
    if (wrapRef.current) wrapRef.current.dataset.tick = String(Date.now());
    // We deliberately don't add `nodes`/`links` to deps — they're memoized off the same source.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.commandCenter]);

  const onNode = (idx: number) => {
    const n = nodes[idx];
    if (n.item) ui.openEntity(n.item);
    // Compute blast-radius set
    const reachable = new Set<number>([idx]);
    let frontier = [idx];
    while (frontier.length) {
      const next: number[] = [];
      for (const i of frontier) {
        for (const l of links) {
          if (l.source === i && !reachable.has(l.target)) {
            reachable.add(l.target);
            next.push(l.target);
          }
        }
      }
      frontier = next;
    }
    setHighlighted(reachable);
  };

  // Esc clears
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape" && highlighted) setHighlighted(null);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [highlighted]);

  if (nodes.length === 0) {
    return (
      <>
        <ContentHeader title={meta.label} description={meta.desc} />
        <div className="empty">No services or dependencies to graph yet.</div>
      </>
    );
  }

  return (
    <>
      <ContentHeader title={meta.label} count={nodes.length} description={meta.desc}
        right={highlighted ? (
          <button className="btn-ghost" onClick={() => setHighlighted(null)}>Clear highlight</button>
        ) : undefined} />
      <GraphLegend />
      <div ref={wrapRef}
           style={{ position: "relative", width: "100%", height: "calc(100dvh - 280px)", minHeight: 480 }}>
        <svg width="100%" height="100%" viewBox={`0 0 ${W} ${H}`}
             role="group"
             aria-label={`Service dependency graph: ${nodes.length} nodes, ${links.length} dependencies. Activate a node to highlight its blast radius.`}
             style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 10 }}>
          <defs>
            <marker id="arr" viewBox="0 0 10 10" refX={9} refY={5}
                    markerWidth={6} markerHeight={6} orient="auto-start-reverse">
              <path d="M0,0 L10,5 L0,10 z" fill="var(--text-faint)" />
            </marker>
          </defs>
          <g>
            {links.map((l, i) => {
              const a = nodes[l.source]; const b = nodes[l.target];
              const dim = highlighted && !(highlighted.has(l.source) && highlighted.has(l.target));
              return (
                <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                      className={`graph-edge${dim ? " dim" : ""}`} markerEnd="url(#arr)" />
              );
            })}
          </g>
          <g>
            {nodes.map((n, i) => {
              const r = n.kind === "service" ? 8 : 6;
              const dim = highlighted && !highlighted.has(i);
              const tierCls = n.tier ? ` tier-${n.tier}` : "";
              return (
                <g key={n.id} transform={`translate(${n.x}, ${n.y})`}
                   className={`graph-node ${n.kind}${tierCls}${dim ? " dim" : ""}`}
                   style={{ cursor: "pointer" }}
                   role="button" tabIndex={0}
                   aria-label={`${n.label} — ${n.kind}${n.tier ? `, ${n.tier}` : ""}. Highlight blast radius.`}
                   onClick={() => onNode(i)}
                   onKeyDown={(e) => {
                     if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onNode(i); }
                   }}>
                  {/* invisible larger hit-target so the ~8px node isn't a pixel-hunt */}
                  <circle r={22} fill="transparent" />
                  <circle r={r} />
                  <text dy={-10} textAnchor="middle" className="graph-label">{n.label}</text>
                </g>
              );
            })}
          </g>
        </svg>
      </div>
    </>
  );
}

function GraphLegend() {
  const items: Array<[string, string]> = [
    ["var(--p0)", "P0"], ["var(--p1)", "P1"], ["var(--p2)", "P2"], ["var(--p3)", "P3"],
    ["var(--accent)", "Integration"], ["var(--text-faint)", "Infra dep"],
  ];
  return (
    <div className="graph-legend" aria-hidden="true">
      {items.map(([color, label]) => (
        <span key={label} className="graph-legend-item">
          <span className="graph-legend-dot" style={{ background: color }} />{label}
        </span>
      ))}
    </div>
  );
}

function buildGraph(cc: ReturnType<typeof useData>["commandCenter"]) {
  if (!cc) return { nodes: [] as GraphNode[], links: [] as GraphLink[] };
  const services = [...cc.estates.backend, ...cc.estates.surfaces];
  const integrations = cc.estates.integrations;
  const known = new Set(services.map((s) => s.name));
  const integrationNames = new Set(integrations.map((i) => i.name));

  const nodes: GraphNode[] = [];
  const idIndex: Record<string, number> = {};
  const add = (n: Omit<GraphNode, "x" | "y" | "vx" | "vy">) => {
    idIndex[n.id] = nodes.length;
    nodes.push({ ...n, x: W / 2 + (Math.random() - 0.5) * W * 0.6,
                       y: H / 2 + (Math.random() - 0.5) * H * 0.6,
                       vx: 0, vy: 0 });
  };
  services.forEach((s: BackendOrSurfaceEntity) => add({
    id: s.name, label: s.name, kind: "service", tier: s.tier ?? null, item: s,
  }));
  integrations.forEach((i: IntegrationEntity) => add({
    id: i.name, label: i.name, kind: "integration", tier: null, item: i,
  }));

  const links: GraphLink[] = [];
  services.forEach((s: BackendOrSurfaceEntity) => {
    (s.dependencies || []).forEach((d) => {
      if (!known.has(d) && !integrationNames.has(d)) {
        if (idIndex[d] === undefined) add({ id: d, label: d, kind: "infra", tier: null });
      }
      const src = idIndex[s.name];
      const tgt = idIndex[d];
      if (src !== undefined && tgt !== undefined) links.push({ source: src, target: tgt });
    });
  });
  return { nodes, links };
}
